<template>
  <el-drawer title="设置食品安全" :visible.sync="visible" @close="handleClose" size="900px">
    <div class="nutrient-drawer">
      <div class="nutrient-header">
        <span>营养成分表</span>
        <span class="nutrient-tips">为帮助消费者决策，营养成分表、配料表会在商品详情页展示，请务必仔细检查并准确填写，避免违规</span>
      </div>

      <el-form :model="form" ref="form" class="nutrient-form" v-if="schemaRuleList.length">
        <!-- 计量单位 -->
        <div v-if="measurementUnitRule" style="padding: 10px 0px;">
          <div class="nutrient-unit-row">
            <span class="unit-label">
              <span v-if="isMeasurementUnitRequired" style="color: #f56c6c; margin-right: 2px">*</span>计量单位
            </span>
            <span class="unit-prefix">{{ measurementUnitRule.prefix }}</span>
            <el-input v-model="measurementUnitValue.value" placeholder="请输入" style="width: 140px" @blur="validateMeasurementUnit" />
            <el-select v-model="measurementUnitValue.unit" style="width: 100px" @change="validateMeasurementUnit">
              <el-option v-for="opt in measurementUnitRule.options" :key="opt" :label="opt" :value="opt" />
            </el-select>
          </div>
          <div v-if="errors.measurementUnit" class="field-error" style="margin-left: 100px;">{{ errors.measurementUnit }}</div>
        </div>

        <!-- 营养成分表格（合并，含展开/收起行） -->
        <el-table :data="allTableData" border style="width: 100%" :span-method="tableSpanMethod" :row-class-name="tableRowClassName">
          <el-table-column label="成分" width="270">
            <template slot-scope="scope">
              <template v-if="scope.row._isToggle">
                <el-button type="link" @click="showOtherNutrients = !showOtherNutrients">
                  {{ showOtherNutrients ? '收起其他营养成分' : '展开其他营养成分' }}
                  <i :class="showOtherNutrients ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                </el-button>
              </template>
              <span v-else :style="{ paddingLeft: scope.row.isChild ? '34px' : '12px' }">
                <span v-if="scope.row.required" style="color: #f56c6c; margin-right: 2px">*</span>{{ scope.row.isChild ? '——' : '' }} {{ scope.row.name }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="含量" min-width="200">
            <template slot-scope="scope">
              <div v-if="!scope.row._isToggle">
                <el-input-digit
                  v-model.number="scope.row.value"
                  placeholder="请输入"
                  :nullable="true"
                  @change="onNutrientChange(scope.row)"
                  @blur="onNutrientBlur(scope.row)"
                  style="width: 85%"
                >
                  <span>{{ scope.row.nrvUnit }}</span>
                </el-input-digit>
                <div v-if="errors[scope.row.attId]" class="field-error">{{ errors[scope.row.attId] }}</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="营养素参考值(NRV)%" min-width="200">
            <template slot="header">
              <span>营养素参考值(NRV)%</span>
              <el-tooltip
                popper-class="nrv-tooltip"
                effect="dark"
                content="NRV%=X/NRV*100%【X】代表食品中某营养素的含量，【NRV】代表该营养项的营养素参考值"
                placement="top"
              >
                <i class="el-icon-question"></i>
              </el-tooltip>
            </template>
            <template slot-scope="scope">
              <template v-if="!scope.row._isToggle">
                <span v-if="scope.row.nrvRef">{{ scope.row.computedNrv || '-' }}</span>
                <span v-else>-</span>
              </template>
            </template>
          </el-table-column>
        </el-table>

        <!-- 配料表 -->
        <div v-if="ingredientRule" class="ingredient-section">
          <div class="ingredient-label">配料表</div>
          <el-input type="textarea" v-model="ingredientData.value" :maxlength="300" :rows="4" resize="none" />
          <div class="ingredient-count">{{ (ingredientData.value || '').length }} / 300</div>
        </div>

        <!-- 产品信息（CPXX 厂商信息） -->
        <div v-if="productInfoRules.length" class="product-info-section">
          <div class="product-info-title">产品信息</div>
          <div class="product-info-list">
            <div v-for="item in productInfoRules" :key="item.attId" class="product-info-item">
              <span class="product-info-name">{{ item.name }}</span>
              <el-input v-model="productInfoData[item.attId]" :placeholder="'请输入' + item.name" style="flex: 1" />
            </div>
          </div>
        </div>
      </el-form>
      <div class="drawer-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" @click="handleSave">确定</el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script>
export default {
  name: 'NutrientContentDialog',
  data() {
    return {
      visible: false,
      opts: {},
      form: {},
      schemaRuleList: [],
      measurementUnitRule: null,
      isMeasurementUnitRequired: false,
      measurementUnitValue: { value: '', unit: '' },
      sortedNutrientRules: [],
      ingredientRule: null,
      ingredientData: { value: '' },
      nutrientDataMap: {},
      requiredNutrientSet: new Set(), // linkRequire='1' 的必填字段 attId
      showOtherNutrients: false,
      // 产品信息（CPXX）
      productInfoRules: [],
      productInfoData: {},
    };
  },
  computed: {
    /** 实时校验错误映射 */
    errors() {
      const errs = {};
      // 计量单位
      if (this.isMeasurementUnitRequired && (!this.measurementUnitValue.value || !this.measurementUnitValue.unit)) {
        errs.measurementUnit = '请输入计量单位';
      }
      // 营养成分
      this.requiredNutrientSet.forEach(attId => {
        const data = this.nutrientDataMap[attId];
        if (!data || !data.value || !String(data.value).trim()) {
          const rule = this.sortedNutrientRules.find(r => r.attId === attId || (r.children && r.children.some(c => c.attId === attId)));
          if (rule) {
            const child = rule.children?.find(c => c.attId === attId);
            errs[attId] = '请输入' + (child ? child.name : rule.name);
          }
        }
      });
      return errs;
    },
    /** 合并的表格数据，含展开/收起行 */
    allTableData() {
      // 必填字段始终在 mainRows
      const requiredRules = this.sortedNutrientRules.filter(r => this.requiredNutrientSet.has(r.attId));
      const optionalRules = this.sortedNutrientRules.filter(r => !this.requiredNutrientSet.has(r.attId));

      const mainRequiredRows = this.buildTableData(requiredRules);
      const remainingSlots = Math.max(0, 5 - mainRequiredRows.length);
      const mainOptionalRows = this.buildTableData(optionalRules.slice(0, remainingSlots));
      const otherRows = this.buildTableData(optionalRules.slice(remainingSlots));

      const mainRows = [...mainRequiredRows, ...mainOptionalRows];
      if (!otherRows.length) return mainRows;
      const toggleRow = { _isToggle: true };
      return this.showOtherNutrients ? [...mainRows, ...otherRows, toggleRow] : [...mainRows, toggleRow];
    },
  },
  methods: {
    method_open(opts) {
      this.opts = opts;
      this.visible = true;
      this.schemaRuleList = JSON.parse(opts.schemaRules?.schemaRule || '[]');
      this.parseSchemaRules();
      this.loadRowData(opts.row);
    },
    /**
     * 表格行样式：展开/收起行加 class
     */
    tableRowClassName({ row }) {
      return row._isToggle ? 'toggle-row' : '';
    },
    /**
     * 表格合并单元格：展开/收起行合并3列居中
     */
    tableSpanMethod({ row, columnIndex }) {
      if (row._isToggle) {
        if (columnIndex === 0) {
          return [1, 3];
        }
        return [0, 0];
      }
    },
    /**
     * 构建表格展示数据
     */
    buildTableData(rules) {
      const rows = [];
      rules.forEach(rule => {
        const data = this.nutrientDataMap[rule.attId] || {};
        const value = data.value || '';
        const nrvRef = rule.features?.nrvRef;
        const nrvUnit = rule.features?.nrvUnit || '';
        let computedNrv = '';
        if (nrvRef && value) {
          const numValue = parseFloat(value);
          if (!isNaN(numValue) && parseFloat(nrvRef)) {
            computedNrv = ((numValue / parseFloat(nrvRef)) * 100).toFixed(0);
          }
        }
        rows.push({
          attId: rule.attId,
          name: rule.name,
          value,
          nrvRef,
          nrvUnit,
          computedNrv,
          isChild: false,
          required: this.requiredNutrientSet.has(rule.attId),
        });
        if (rule.children && rule.children.length) {
          rule.children.forEach(child => {
            const childData = this.nutrientDataMap[child.attId] || {};
            const childValue = childData.value || '';
            const childNrvRef = child.features?.nrvRef;
            const childNrvUnit = child.features?.nrvUnit || '';
            let childComputedNrv = '';
            if (childNrvRef && childValue) {
              const childNumValue = parseFloat(childValue);
              if (!isNaN(childNumValue) && parseFloat(childNrvRef)) {
                childComputedNrv = ((childNumValue / parseFloat(childNrvRef)) * 100).toFixed(0);
              }
            }
            rows.push({
              attId: child.attId,
              name: child.name,
              value: childValue,
              nrvRef: childNrvRef,
              nrvUnit: childNrvUnit,
              computedNrv: childComputedNrv,
              isChild: true,
              required: this.requiredNutrientSet.has(child.attId),
            });
          });
        }
      });
      return rows;
    },
    onNutrientChange(row) {
      this.$set(this.nutrientDataMap, row.attId, { value: row.value });
    },
    onNutrientBlur(row) {
      if (this.requiredNutrientSet.has(row.attId)) {
        this.$forceUpdate();
      }
    },
    validateMeasurementUnit() {
      if (this.isMeasurementUnitRequired) {
        this.$forceUpdate();
      }
    },
    parseSchemaRules() {
      const yyRules = [];
      const cpRules = []; // CPXX 产品信息
      let measurementUnitRule = null;
      let ingredientRule = null;
      const requiredNutrientSet = new Set();
      let isMeasurementUnitRequired = false;

      this.schemaRuleList.forEach(rule => {
        // 计量单位
        if (rule.tenant === 'YYCF' && rule.inputType === 18) {
          isMeasurementUnitRequired = rule.features?.linkRequire === '1';
          measurementUnitRule = {
            prefix: '',
            options: [],
            attId: rule.attId,
            name: rule.name,
            enName: rule.features?.enName || 'jldw',
          };
          if (rule.categoryGroupAttExtValueTemplate?.attrValues) {
            rule.categoryGroupAttExtValueTemplate.attrValues.forEach(v => {
              if (v.type === 3 && v.prefix) measurementUnitRule.prefix = v.prefix;
              if (v.type === 4 && v.options) measurementUnitRule.options = v.options;
            });
          }
          return;
        }

        // 配料表
        if (rule.tenant === 'PLCF') {
          ingredientRule = {
            attId: rule.attId,
            name: rule.name,
            enName: rule.features?.enName || 'plcf',
            index: rule.index,
          };
          return;
        }

        // 营养成分
        if (rule.tenant === 'YYCF' && rule.inputType === 7) {
          // linkRequire === '1' 标记为必填
          if (rule.features?.linkRequire === '1') {
            requiredNutrientSet.add(rule.attId);
          }
          const item = {
            attId: rule.attId,
            name: rule.name,
            features: rule.features,
            tenant: rule.tenant,
            index: rule.index || 0,
            children: [],
          };
          if (rule.childrenCustomProps) {
            item.children = rule.childrenCustomProps.map(child => {
              if (child.features?.linkRequire === '1') {
                requiredNutrientSet.add(child.attId);
              }
              return {
                attId: child.attId,
                name: child.name,
                features: child.features,
                tenant: child.tenant || 'YYCF',
                index: child.index || 0,
              };
            });
          }
          yyRules.push(item);
          return;
        }

        // 产品信息（CPXX）
        if (rule.tenant === 'CPXX') {
          cpRules.push({
            attId: rule.attId,
            name: rule.name,
            enName: rule.features?.enName || '',
            tenant: rule.tenant,
            index: rule.index || 0,
          });
        }
      });

      // 按 tenant + index 排序
      yyRules.sort((a, b) => {
        if (a.tenant !== b.tenant) return a.tenant.localeCompare(b.tenant);
        return (a.index || 0) - (b.index || 0);
      });
      cpRules.sort((a, b) => (a.index || 0) - (b.index || 0));

      this.measurementUnitRule = measurementUnitRule;
      this.isMeasurementUnitRequired = isMeasurementUnitRequired;
      this.ingredientRule = ingredientRule;
      this.sortedNutrientRules = yyRules;
      this.productInfoRules = cpRules;
      this.requiredNutrientSet = requiredNutrientSet;

      // 初始化产品数据
      cpRules.forEach(item => {
        this.$set(this.productInfoData, item.attId, '');
      });
    },
    loadRowData(row) {
      if (!row || !row.nutrientContent || !row.nutrientContent.length) return;
      const nc = row.nutrientContent;

      nc.forEach(item => {
        const attId = item.attrId;
        const customAttrValues = item.customAttrValues;

        // 计量单位
        if (this.measurementUnitRule && attId === this.measurementUnitRule.attId) {
          if (customAttrValues?.length && customAttrValues[0].complexExpands) {
            customAttrValues[0].complexExpands.forEach(expand => {
              if (expand.index === 1 && expand.prefix) {
                this.measurementUnitValue.value = expand.expand || '';
              }
              if (expand.index === 2) {
                this.measurementUnitValue.unit = expand.expand || '';
              }
            });
          }
          return;
        }

        // 配料表
        if (this.ingredientRule && attId === this.ingredientRule.attId) {
          if (customAttrValues?.length) {
            this.ingredientData.value = customAttrValues[0].attrValue || '';
          }
          return;
        }

        // 产品信息
        const piRule = this.productInfoRules.find(p => p.attId === attId);
        if (piRule) {
          if (customAttrValues?.length) {
            this.$set(this.productInfoData, attId, customAttrValues[0].attrValue || '');
          }
          return;
        }

        // 营养成分
        const nRule = this.sortedNutrientRules.find(r => r.attId === attId || (r.children && r.children.some(c => c.attId === attId)));
        if (nRule) {
          if (nRule.attId === attId && customAttrValues?.length) {
            this.$set(this.nutrientDataMap, attId, { value: customAttrValues[0].attrValue || '' });
          } else if (nRule.children) {
            const child = nRule.children.find(c => c.attId === attId);
            if (child && customAttrValues?.length) {
              this.$set(this.nutrientDataMap, attId, { value: customAttrValues[0].attrValue || '' });
            }
          }
        }
      });
    },
    validateRequired() {
      const missing = [];
      if (this.isMeasurementUnitRequired && (!this.measurementUnitValue.value || !this.measurementUnitValue.unit)) {
        missing.push('计量单位');
      }
      this.requiredNutrientSet.forEach(attId => {
        const data = this.nutrientDataMap[attId];
        if (!data || !data.value || !String(data.value).trim()) {
          const rule = this.sortedNutrientRules.find(r => r.attId === attId || (r.children && r.children.some(c => c.attId === attId)));
          if (rule) {
            const child = rule.children?.find(c => c.attId === attId);
            missing.push(child ? child.name : rule.name);
          } else {
            missing.push(attId);
          }
        }
      });
      if (missing.length) {
        return { valid: false, msg: `请完善必填项：${missing.join('、')}` };
      }
      return { valid: true };
    },
    buildCustomPropsMode(tenant) {
      return tenant + 'SX';
    },
    buildNutrientContent() {
      const result = [];

      // 计量单位
      if (this.measurementUnitRule) {
        result.push({
          attrId: this.measurementUnitRule.attId,
          name: this.measurementUnitRule.name,
          customAttrValues: [
            {
              complexExpands: [
                { expand: this.measurementUnitValue.value, index: 1, prefix: this.measurementUnitRule.prefix },
                { expand: this.measurementUnitValue.unit, index: 2 },
              ],
              index: 1,
            },
          ],
          customPropsMode: this.buildCustomPropsMode('YYCF'),
          features: [{ key: 'enName', value: this.measurementUnitRule.enName }],
        });
      }

      // 配料表
      if (this.ingredientRule) {
        result.push({
          attrId: this.ingredientRule.attId,
          name: this.ingredientRule.name,
          customAttrValues: [{ attrValue: this.ingredientData.value || '' }],
          customPropsMode: this.buildCustomPropsMode('PLCF'),
          features: [{ key: 'enName', value: this.ingredientRule.enName }],
          index: this.ingredientRule.index,
        });
      }

      // 营养成分
      this.sortedNutrientRules.forEach(rule => {
        const data = this.nutrientDataMap[rule.attId];
        if (data && data.value) {
          result.push({
            attrId: rule.attId,
            name: rule.name,
            customAttrValues: [{ attrValue: data.value }],
            customPropsMode: this.buildCustomPropsMode(rule.tenant),
            features: [{ key: 'enName', value: rule.features?.enName || '' }],
            index: rule.index,
          });
        }
        if (rule.children) {
          rule.children.forEach(child => {
            const childData = this.nutrientDataMap[child.attId];
            if (childData && childData.value) {
              result.push({
                attrId: child.attId,
                name: child.name,
                customAttrValues: [{ attrValue: childData.value }],
                customPropsMode: this.buildCustomPropsMode(child.tenant),
                features: [{ key: 'enName', value: child.features?.enName || '' }],
                index: child.index,
              });
            }
          });
        }
      });

      // 产品信息
      this.productInfoRules.forEach(item => {
        const val = this.productInfoData[item.attId];
        if (val) {
          result.push({
            attrId: item.attId,
            name: item.name,
            customAttrValues: [{ attrValue: val }],
            customPropsMode: this.buildCustomPropsMode(item.tenant),
            features: [{ key: 'enName', value: item.enName }],
            index: item.index,
          });
        }
      });

      return result;
    },
    handleSave() {
      const linkValidate = this.validateRequired();
      if (!linkValidate.valid) {
        this.$message.warning(linkValidate.msg);
        return;
      }
      this.opts.sureFn(this.buildNutrientContent());
      this.handleClose();
    },
    handleClose() {
      this.visible = false;
      this.schemaRuleList = [];
      this.sortedNutrientRules = [];
      this.measurementUnitRule = null;
      this.ingredientRule = null;
      this.measurementUnitValue = { value: '', unit: '' };
      this.ingredientData = { value: '' };
      this.nutrientDataMap = {};
      this.requiredNutrientSet = new Set();
      this.productInfoRules = [];
      this.productInfoData = {};
      this.showOtherNutrients = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.nutrient-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  .nutrient-tips {
    margin-left: 12px;
    color: #909399;
    font-size: 12px;
  }
}

.nutrient-form {
  ::v-deep .el-form-item {
    margin-bottom: 0;
  }
}

.nutrient-unit-row {
  display: flex;
  align-items: center;
  .unit-label {
    width: 80px;
    flex-shrink: 0;
  }
  .unit-prefix {
    margin: 0 8px 0 0;
    color: #606266;
  }
}

.field-error {
  color: #f56c6c;
  font-size: 12px;
  line-height: 1;
  margin-top: 4px;
}

.ingredient-section {
  padding: 12px;
  margin-top: 12px;
  border: 1px solid #ebeef5;
  border-top: none;
  .ingredient-label {
    margin-bottom: 8px;
    font-weight: 500;
  }
  .ingredient-count {
    text-align: right;
    color: #909399;
    font-size: 12px;
    margin-top: 4px;
  }
}

.product-info-section {
  padding: 12px;
  margin-top: 12px;
  border: 1px solid #ebeef5;
  border-top: none;
  .product-info-title {
    margin-bottom: 12px;
    font-weight: 500;
    font-size: 14px;
  }
  .product-info-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  .product-info-item {
    display: flex;
    align-items: center;
    width: calc(50% - 6px);
    .product-info-name {
      width: 100px;
      flex-shrink: 0;
      margin-right: 8px;
      color: #606266;
      font-size: 14px;
    }
  }
}

::v-deep .el-drawer__body {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* 展开/收起行居中 */
::v-deep .el-table tr.toggle-row td {
  text-align: center;
  padding: 4px;
}

::v-deep .el-table .cell {
  overflow: visible;
}

.nutrient-drawer {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  .nutrient-header {
    flex-shrink: 0;
    padding: 20px 20px 0;
  }
  .nutrient-form {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0 20px;
  }
}

.drawer-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}
</style>
